import omit from "lodash/omit";
import cloneDeep from "lodash/cloneDeep";

import {
  ADDRESS,
  BUDGET_CHAPTER,
  BUDGET_DISPATCHER,
  CHECKBOXES_SELECT,
  EMAIL_WITH_CONSENTS,
  EVENTS,
  FILE,
  FILES,
  HTML,
  ORAL_INTERVIEW,
  RADIOS,
  SELECT,
  SORTABLE_LIST,
  STRING,
  TAXONOMIES,
  THEMES
} from "../components/Form/SchemaTypes";
import { sortBy } from "lodash";

// Calculates the actual value of a field depending on its field type, by fetching the
// corresponding data in the provided `form` redux store substate.
//
export default function getFormFieldValue(form, fieldName) {
  const value = form.fields[fieldName];
  const schema = form.schema[fieldName];

  // Allow calling this function on an unitialized field
  if (!schema) return null;

  switch (schema.type) {
    // Clean #files key from file input generated data
    case FILES:
      return value.files.map(file => file.key);

    // Clean process keys from single file input generated data to match the
    // default multi-files expected input
    case FILE:
      return omit({ ...value, blobKeys: [value.key] }, ["key", "file"]);

    case STRING:
    case HTML:
    case ORAL_INTERVIEW:
      return value.value;

    case EMAIL_WITH_CONSENTS: {
      return {
        content: value.value,
        consentGiven: !!value.consentGiven
      };
    }

    case THEMES:
    case TAXONOMIES:
      return value.values || (value.value && [value.value]);

    case EVENTS:
      return value.values || (value?.value?.value && [value?.value?.value]);

    case BUDGET_DISPATCHER:
      return value.values;

    case BUDGET_CHAPTER:
      return value.values.map(({ id, optionIds }) => ({
        budgetLineId: id,
        optionIds
      }));

    case SORTABLE_LIST:
      return sortBy(value.values, ({ position }) => position).map(({ id }) => id);

    case ADDRESS:
      return schema.fieldNames.reduce(
        (answers, fieldName) => ({
          ...answers,
          [fieldName]: getFormFieldValue(form, fieldName)
        }),
        {}
      );

    // Extract the value from the { value, label } pair needed as value by the
    // underlying react-select component
    case SELECT:
      return value?.value?.value;

    case CHECKBOXES_SELECT: {
      const { other, otherValue } = value;
      return { values: value?.value?.value, other, otherValue };
    }

    // Radio buttons use a `value` local value store name, but are serialized into check
    // boxes `values` on the server as they're from the same FormField source but with a
    // maximumSelectableOptions attribute of 1
    case RADIOS:
      return omit({ ...value, values: value?.value }, ["value"]);

    // Ensure we use a cloned value to avoid any update of the original object
    default:
      return cloneDeep(value);
  }
}
